import { apiRequest } from './client.js';

/**
 * Company (M01 §9 Company Master + §10 status engine) — real /v1/companies contract.
 * Tenant is derived from the caller's session on the backend.
 */

export const COMPANY_STATUSES = [
  'ACTIVE',
  'INACTIVE',
  'SUSPENDED',
  'ARCHIVED',
] as const;
export type CompanyStatus = (typeof COMPANY_STATUSES)[number];

export const COMPANY_STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  SUSPENDED: 'Suspended',
  ARCHIVED: 'Archived',
};

/** T002 company record. */
export interface Company {
  readonly id: string;
  readonly tenantId?: string;
  readonly companyCode: string;
  readonly companyName: string;
  readonly legalName?: string | null;
  readonly panNumber?: string | null;
  readonly country?: string | null;
  readonly state?: string | null;
  readonly district?: string | null;
  readonly pincode?: string | null;
  readonly address?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  readonly website?: string | null;
  readonly status: CompanyStatus;
  readonly effectiveFromDate?: string | null;
  readonly effectiveToDate?: string | null;
  readonly version: number;
}

export interface CreateCompanyInput {
  readonly companyCode: string;
  readonly companyName: string;
  readonly legalName: string;
  readonly panNumber?: string;
  readonly country?: string;
  readonly state?: string;
  readonly district?: string;
  readonly pincode?: string;
  readonly address?: string;
  readonly phone?: string;
  readonly email?: string;
  readonly website?: string;
  /** ISO 8601 datetime with timezone. */
  readonly effectiveFrom?: string;
  readonly effectiveTo?: string;
}

export interface UpdateCompanyInput {
  readonly companyName?: string;
  readonly legalName?: string;
  readonly panNumber?: string;
  readonly country?: string;
  readonly state?: string;
  readonly district?: string;
  readonly pincode?: string;
  readonly address?: string;
  readonly phone?: string;
  readonly email?: string;
  readonly website?: string;
  readonly effectiveFrom?: string;
  readonly effectiveTo?: string;
  readonly version: number;
}

export interface TransitionStatusInput {
  readonly toStatus: CompanyStatus;
  readonly reason: string;
  readonly version: number;
}

export interface CompanyStatusHistoryRow {
  readonly id: string;
  readonly fromStatus: string;
  readonly toStatus: string;
  readonly reason: string;
  readonly occurredAt?: string;
}

type Tok = string | null | undefined;

function unwrap(
  r: { items: readonly Company[] } | readonly Company[] | null,
): readonly Company[] {
  if (!r) return [];
  return Array.isArray(r)
    ? r
    : (r as { items: readonly Company[] }).items ?? [];
}

/** GET /v1/companies — live companies for the current tenant. */
export async function listCompanies(token?: Tok): Promise<readonly Company[]> {
  const r = await apiRequest<{ items: readonly Company[] } | readonly Company[]>(
    '/companies',
    { token },
  );
  return unwrap(r);
}

/** GET /v1/companies/:id — a single company. */
export function getCompany(id: string, token?: Tok): Promise<Company> {
  return apiRequest<Company>(`/companies/${encodeURIComponent(id)}`, { token });
}

/** POST /v1/companies — create a company (§9 full field set). */
export function createCompany(
  body: CreateCompanyInput,
  token?: Tok,
): Promise<Company> {
  return apiRequest<Company>('/companies', { method: 'POST', token, body });
}

/** PATCH /v1/companies/:id — update (optimistic concurrency via version). */
export function updateCompany(
  id: string,
  body: UpdateCompanyInput,
  token?: Tok,
): Promise<Company> {
  return apiRequest<Company>(`/companies/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    token,
    body,
  });
}

/** POST /v1/companies/:id/status — §10 status transition. */
export function transitionCompanyStatus(
  id: string,
  body: TransitionStatusInput,
  token?: Tok,
): Promise<Company> {
  return apiRequest<Company>(
    `/companies/${encodeURIComponent(id)}/status`,
    { method: 'POST', token, body },
  );
}

/** GET /v1/companies/:id/status-history — §10 status ledger. */
export function getCompanyStatusHistory(
  id: string,
  token?: Tok,
): Promise<readonly CompanyStatusHistoryRow[]> {
  return apiRequest<readonly CompanyStatusHistoryRow[]>(
    `/companies/${encodeURIComponent(id)}/status-history`,
    { token },
  ).then((r) => r ?? []);
}
